import { Dialog, DialogContent, DialogTitle, Grid } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react';
import { ActionButton } from 'core/components/action-button';
import { captureException } from '@sentry/react';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import disclaimerIcon from 'core/assets/svg/alert-dark-green.svg';
import closeIcon from 'core/assets/svg/x.svg';
import Label from 'core/components/label/label';
import useStyles from './assurity-life-modal.styles';
import Button from 'core/components/button/button';
import { useToast } from 'core/components/toast';
import { gql, useMutation } from '@apollo/client';
import { FormField } from 'core/components/form';
import { currencyFormatter } from 'core/helpers/formatters';
import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';

const createAndSendCaseMutation = gql`
  mutation SendLinkMutation(
    $branchOfferId: String
    $branchOfferEmail: String
    $branchPolicyId: String
    $personId: String!
  ) {
    assurityLifeCreateAndSendCase(
      args: {
        branchOfferId: $branchOfferId
        branchOfferEmail: $branchOfferEmail
        branchPolicyId: $branchPolicyId
        personId: $personId
      }
    ) {
      __typename
      ... on AssurityLifeCreateAndSendCaseErrorResponse {
        message
      }

      ... on AssurityLifeCreateAndSendCaseSuccessResponse {
        message
      }
    }
  }
`;

const createCaseToOpenMutation = gql`
  mutation CreateCaseToOpenMutation(
    $branchOfferId: String
    $branchOfferEmail: String
    $branchPolicyId: String
    $personId: String!
  ) {
    assurityLifeCreateCaseToOpen(
      args: {
        branchOfferId: $branchOfferId
        branchOfferEmail: $branchOfferEmail
        branchPolicyId: $branchPolicyId
        personId: $personId
      }
    ) {
      __typename
      ... on AssurityLifeCreateCaseToOpenErrorResponse {
        message
      }

      ... on AssurityLifeCreateCaseToOpenSuccessResponse {
        agentLink
      }
    }
  }
`;

const validation = yup.object().shape({
  email: yup.string('Please enter a valid email').email('Please enter a valid email').required('An email is required'),
  acceptedCommunicationConsent: yup.boolean().oneOf([true], 'You must accept the communication consent')
});

const AssurityLifeModal = observer(function AssurityLifeModal({ open, onClose, assurityData, people }) {
  const classes = useStyles();
  const toast = useToast();
  const {
    account: { policies, id, sendEventAsMember },
    offer
  } = useStore();
  const {
    user: { affinityCode }
  } = useSession();

  const location = window.location.href;
  const webUserId = offer?.offer?.webUserId;

  const determineIfPrimary = (personId) => {
    return offer?.offer?.quote?.drivers.some((driver) => driver.id === personId && driver.isPrimary);
  };

  const peopleOptions = assurityData.eligibleLifeOffers?.map((offer) => {
    const person = people.find((p) => p.id === offer.personId);
    return {
      id: person.id,
      value: `${person.firstName} ${person.lastName}`
    };
  });

  const pricesMap = assurityData.eligibleLifeOffers?.reduce((acc, offer) => {
    acc[offer.personId] = {
      ...offer,
      name: `${people.find((p) => p.id === offer.personId).firstName}`
    };
    return acc;
  }, {});

  useEffect(() => {
    if (open) {
      const currentPersonId = assurityData.eligibleLifeOffers?.[0]?.personId;
      const amount = currencyFormatter(pricesMap[currentPersonId]?.benefitAmount);

      const sendSegmentEvent = async () => {
        try {
          await sendEventAsMember({
            eventName: 'Staff Life Insurance Price Previewed',
            userId: webUserId,
            properties: JSON.stringify({
              amount,
              isPrimary: determineIfPrimary(currentPersonId)
            })
          });
        } catch {}
      };

      sendSegmentEvent();
    }
  }, []);

  const [createAndSendCase, createAndSendCaseResult] = useMutation(createAndSendCaseMutation);
  const [createCaseToOpen, createCaseToOpenResult] = useMutation(createCaseToOpenMutation);

  const getPolicyIdFromPersonId = (personId) => {
    return policies.list.find(
      (p) =>
        p.policyDetails.drivers?.some((d) => d.id === personId) ||
        p.policyDetails.people.some((d) => d.id === personId) ||
        p.policyDetails.nonDrivers?.some((d) => d.id === personId)
    ).id;
  };

  const sendLink = async ({ email, selectedPerson }) => {
    try {
      const result = await createAndSendCase({
        variables: {
          branchOfferId: assurityData?.branchOfferId || '',
          branchPolicyId: assurityData?.branchOfferId ? '' : getPolicyIdFromPersonId(selectedPerson),
          branchOfferEmail: email,
          personId: selectedPerson
        }
      });
      switch (result.data?.assurityLifeCreateAndSendCase.__typename) {
        case 'AssurityLifeCreateAndSendCaseErrorResponse':
          toast.notify({ type: 'error', message: result.data.assurityLifeCreateAndSendCase.message });
          break;
        case 'AssurityLifeCreateAndSendCaseSuccessResponse':
          toast.notify({ type: 'success', message: result.data.assurityLifeCreateAndSendCase.message });
          break;
        default:
          break;
      }
      // email was updated
      if (assurityData?.branchOfferId && !assurityData.email && email) {
        offer.getOffer(assurityData?.branchOfferId);
      }

      track('Agent Sent Assurity Link', {
        URL: location,
        preBindId: webUserId,
        memberAccountId: id,
        affinityCode,
        isPrimary: determineIfPrimary(selectedPerson)
      });

      onClose();
    } catch (e) {
      captureException(e);
      toast.notify({ type: 'error', message: 'An error occurred while sending the link' });
    }
  };

  const openOffer = async ({ selectedPerson, email }) => {
    // kind of weird but we have to figure out which policy to open .. could be multiple h/a policies. so find the corresponding policy of the selected person id

    try {
      const result = await createCaseToOpen({
        variables: {
          personId: selectedPerson,
          branchOfferId: assurityData?.branchOfferId || '',
          branchPolicyId: assurityData?.branchOfferId ? '' : getPolicyIdFromPersonId(selectedPerson),
          branchOfferEmail: email
        }
      });
      switch (result.data?.assurityLifeCreateCaseToOpen.__typename) {
        case 'AssurityLifeCreateCaseToOpenErrorResponse':
          toast.notify({ type: 'error', message: result.data.assurityLifeCreateCaseToOpen.message });
          break;
        case 'AssurityLifeCreateCaseToOpenSuccessResponse':
          window.open(result.data.assurityLifeCreateCaseToOpen.agentLink, '_blank');
          break;
        default:
          break;
      }
      if (assurityData?.branchOfferId && !assurityData.email && email) {
        offer.getOffer(assurityData?.branchOfferId);
      }

      track('Agent Bridged to Assurity', {
        URL: location,
        preBindId: webUserId,
        memberAccountId: id,
        affinityCode,
        isPrimary: determineIfPrimary(selectedPerson)
      });

      onClose();
    } catch (e) {
      captureException(e);
      toast.notify({ type: 'error', message: 'An error occurred while opening the offer' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.container }}>
      <ActionButton type="edit" size="big" icon={closeIcon} onClick={onClose} className={classes.closeButton} />
      <DialogTitle>
        <Label className={classes.title}>Instant Term Life Insurance</Label>
        <Label className={classes.subtitle}>From Assurity, quoted through Branch</Label>
      </DialogTitle>
      <DialogContent>
        <p className={classes.text}>
          Branch is partnering with Assurity to offer no-exam term life insurance, quoted within Branch's platform.
          Life-licensed agents can view the offer and send potential members an email to apply online in minutes,
          without a medical exam, and receive an instant decision. Policy auto renews after five years with a new
          premium rate.
          <br />
          <br />
          The benefit amount is editable (up to $500,000) after opening the offer link.
        </p>

        <div>
          <Formik
            initialValues={{
              selectedPerson: assurityData.eligibleLifeOffers?.[0]?.personId,
              acceptedCommunicationConsent: false,
              email: assurityData.email || ''
            }}
            validationSchema={validation}
            enableReinitialize
          >
            {({ values, setErrors, setTouched, validateForm }) => (
              <Form>
                <Grid container>
                  <span className={classes.subContentContainer}>
                    <Label type="infoSubtitle" marginBottom={16}>
                      Whose offer would you like to see?
                    </Label>
                    <FormField
                      mode="light"
                      name={'selectedPerson'}
                      label="Eligible Offers"
                      type="select"
                      options={peopleOptions}
                      xs={12}
                      fast={false}
                    />
                  </span>

                  <div>
                    <p className={classes.priceLine}>
                      {pricesMap[values.selectedPerson].name}'s term life offer for{' '}
                      <strong>{currencyFormatter(pricesMap[values.selectedPerson]?.benefitAmount)}</strong> of coverage
                      is <strong>{currencyFormatter(pricesMap[values.selectedPerson]?.monthlyPremiumAmount)}/mo</strong>{' '}
                      or <strong>{currencyFormatter(pricesMap[values.selectedPerson]?.annualPremiumAmount)}/yr</strong>
                    </p>

                    <div className={classes.disclaimerContainer}>
                      <img src={disclaimerIcon} className={classes.disclaimerIcon} alt="" />
                      <p className={classes.disclaimerText}>
                        This offer price is based on the currently provided applicant details, assuming no tobacco use.
                        The final price is subject to change based on additional details.
                      </p>
                    </div>
                  </div>
                  <span className={classes.subContentContainer}>
                    <Label type="infoSubtitle" marginBottom={4}>
                      Interested in instant term life insurance?
                    </Label>
                    <div className={classes.communicationConsentText}>
                      <FormField
                        type="checkbox"
                        mode="light"
                        name="acceptedCommunicationConsent"
                        label={
                          'The client agrees to receive emails and/or text messages regarding term life insurance. Message and data rates may apply. Message frequency varies.'
                        }
                      />
                    </div>

                    <div>
                      <div className={classes.subContainerRow}>
                        {assurityData.email ? (
                          <Button
                            variant="contained"
                            color="primary"
                            className={classes.subContainerButton}
                            disabled={createAndSendCaseResult.loading || createCaseToOpenResult.loading}
                            onClick={async () => {
                              const errors = await validateForm();
                              if (Object.keys(errors).length) {
                                setErrors(errors);
                                setTouched(errors);
                              } else {
                                sendLink(values);
                              }
                            }}
                          >
                            Send Link {assurityData.email ? `to ${assurityData.email}` : ''}
                          </Button>
                        ) : (
                          <>
                            <FormField
                              className={classes.subContainerButton}
                              mode="light"
                              name={'email'}
                              label="Email"
                              type="string"
                              xs={6}
                              fast={false}
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              className={classes.subContainerButton}
                              disabled={createAndSendCaseResult.loading || createCaseToOpenResult.loading}
                              type="submit"
                              onClick={async () => {
                                const errors = await validateForm();
                                if (Object.keys(errors).length) {
                                  setErrors(errors);
                                  setTouched(errors);
                                } else {
                                  sendLink(values);
                                }
                              }}
                            >
                              Send Link
                            </Button>
                          </>
                        )}
                        {assurityData?.staffUserCapabilities?.includes('CAN_VIEW') && (
                          <>
                            <p className={classes.orText}>OR</p>
                            <Button
                              variant="contained"
                              color="secondary"
                              className={classes.subContainerButton}
                              disabled={createAndSendCaseResult.loading || createCaseToOpenResult.loading}
                              onClick={async () => {
                                const errors = await validateForm();
                                if (Object.keys(errors).length) {
                                  setErrors(errors);
                                  setTouched(errors);
                                } else {
                                  openOffer(values);
                                }
                              }}
                            >
                              Open Offer
                            </Button>
                          </>
                        )}
                      </div>
                      {!assurityData.email ? (
                        <div className={classes.disclaimerContainer}>
                          <img src={disclaimerIcon} className={classes.disclaimerIcon} alt="" />
                          <p className={classes.disclaimerText}>
                            This will update the primary email on the Branch offer. The servicing email for the life
                            policy can be changed during the application process.
                          </p>
                        </div>
                      ) : (
                        <div className={classes.disclaimerContainer}>
                          <img src={disclaimerIcon} className={classes.disclaimerIcon} alt="" />
                          <p className={classes.disclaimerText}>
                            We will send all offers to primary email on file for the
                            {assurityData.branchOfferId ? ' offer' : ' policy'}. The servicing email for the life policy
                            can be changed during the application process.
                          </p>
                        </div>
                      )}
                    </div>
                  </span>
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
});

AssurityLifeModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  assurityData: PropTypes.shape({
    branchOfferId: PropTypes.string,
    email: PropTypes.string,
    branchPolicyId: PropTypes.string,
    staffUserCapabilities: PropTypes.arrayOf(PropTypes.string).isRequired,
    eligibleLifeOffers: PropTypes.arrayOf(
      PropTypes.shape({
        personId: PropTypes.string.isRequired,
        benefitAmount: PropTypes.number.isRequired,
        monthlyPremiumAmount: PropTypes.number.isRequired,
        annualPremiumAmount: PropTypes.number.isRequired
      })
    )
  }),
  people: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string
  })
};

export { AssurityLifeModal };
